import type { APISharedProfileResponse } from '@see-stella/types'
import { SkinMetricGroupTechNameMap } from '@see-stella/types'
import { useMemo, useState } from 'react'
import { labelForMetric } from './labels'
import { metricColorVar } from './model'
import type { ReportActions, ReportState } from './useReportState'
import './MetricSwitcher.css'

interface MetricSwitcherProps {
  profile: APISharedProfileResponse
  state: ReportState
  actions: ReportActions
}

// Category display order + labels (mirrors the app's Deep Dive categories).
const CATEGORY_ORDER = [
  'skin_sensitivity',
  'clarity_breakouts',
  'tone_evenness',
  'structure_aging',
  'eye_area',
  'baseline_properties',
] as const
const CATEGORY_LABELS: Record<string, string> = {
  skin_sensitivity: 'Sensitivity',
  clarity_breakouts: 'Clarity & breakouts',
  tone_evenness: 'Tone & evenness',
  structure_aging: 'Structure & aging',
  eye_area: 'Eye area',
  baseline_properties: 'Skin basics',
}

/** techName -> category group, from the shared authoritative map. */
const GROUP_OF: Record<string, string> = (() => {
  const out: Record<string, string> = {}
  for (const [group, techs] of Object.entries(SkinMetricGroupTechNameMap)) {
    for (const t of techs as string[]) out[t] = group
  }
  return out
})()

/**
 * The single global metric switcher: exactly one metric is active, and the
 * photo, chart, and definition all follow it. Metrics are grouped into the
 * familiar categories (a new viewer who doesn't know the metric names can browse
 * by concern), with a search box to jump straight to one.
 */
export function MetricSwitcher({
  profile,
  state,
  actions,
}: MetricSwitcherProps) {
  const all = useMemo(
    () =>
      profile.metrics.map((m) => ({
        techName: m.techName,
        label: labelForMetric(m.techName),
        group: GROUP_OF[m.techName] ?? 'baseline_properties',
      })),
    [profile]
  )

  // categories that actually have metrics in this profile, in canonical order
  const categories = useMemo(() => {
    const present = new Set(all.map((m) => m.group))
    return CATEGORY_ORDER.filter((g) => present.has(g))
  }, [all])

  const selectedGroup = all.find((m) => m.techName === state.metric)?.group
  // Active category defaults to the selected metric's group so the row always
  // shows the current metric; a search picks then re-syncs the category below.
  const [category, setCategory] = useState<string>(selectedGroup ?? 'all')
  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()

  // search overrides category (a global find); else filter by the active category
  const shown = q
    ? all.filter((m) => m.label.toLowerCase().includes(q))
    : category === 'all'
      ? all
      : all.filter((m) => m.group === category)

  // Selecting a metric (from a chip or a search hit) also syncs the category to
  // its group and clears the search, so the chosen metric never vanishes from
  // the row when the query is cleared.
  const selectMetric = (techName: string, group: string) => {
    actions.setMetric(techName)
    setCategory(group)
    setQuery('')
  }

  return (
    <div className='switch'>
      <div className='switch__head'>
        <span className='switch__label'>Metric</span>
        <input
          className='switch__search'
          type='search'
          inputMode='search'
          placeholder='Search metrics'
          aria-label='Search metrics'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {!q && categories.length > 1 && (
        <div className='switch__cats' role='group' aria-label='Metric category'>
          <button
            type='button'
            aria-pressed={category === 'all'}
            className={`cat${category === 'all' ? ' cat--on' : ''}`}
            onClick={() => setCategory('all')}
          >
            All
          </button>
          {categories.map((g) => (
            <button
              key={g}
              type='button'
              aria-pressed={category === g}
              className={`cat${category === g ? ' cat--on' : ''}${g === selectedGroup ? ' cat--has' : ''}`}
              onClick={() => setCategory(g)}
            >
              {CATEGORY_LABELS[g] ?? g}
            </button>
          ))}
        </div>
      )}

      <div className='switch__row' role='group' aria-label='Choose a metric'>
        {shown.map((m) => {
          const on = m.techName === state.metric
          return (
            <button
              key={m.techName}
              type='button'
              aria-pressed={on}
              className={`chip${on ? ' chip--on' : ''}`}
              style={
                {
                  '--dot-color': metricColorVar(m.techName),
                } as React.CSSProperties
              }
              onClick={() => selectMetric(m.techName, m.group)}
            >
              <span className='chip__sw' />
              {m.label}
            </button>
          )
        })}
        {shown.length === 0 && (
          <span className='switch__empty'>No metric matches “{query}”.</span>
        )}
      </div>
    </div>
  )
}
